import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, FileText, Newspaper, Target, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#141F48] text-white pt-32 pb-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(211,188,141,0.1) 10px, rgba(211,188,141,0.1) 20px)' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {t('heroTitle')}
              </h1>
              <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                {t('heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/divisions">
                  <Button size="lg" className="bg-[#D3BC8D] text-[#141F48] hover:bg-[#c9b180] font-semibold">
                    {t('exploreDivisions')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="/documents/ALMAD-Corporate-Presentation-Rev0.pdf" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#141F48] font-semibold">
                    {t('downloadProfile')}
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-lg shadow-2xl overflow-hidden">
                <img
                  src="/assets/images/corporate-building.jpg"
                  alt="Modern architecture representing ALMAD excellence"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services & Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-[#D3BC8D] hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-[#141F48] rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-[#D3BC8D]" />
                </div>
                <h3 className="text-2xl font-bold text-[#141F48] mb-4">Our Services</h3>
                <p className="text-gray-600 leading-relaxed">
                  We offer a tailored range of reliable and competitive EPC services, ensuring specialized attention and precision for every client's unique goals.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#D3BC8D] hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-[#141F48] rounded-lg flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-[#D3BC8D]" />
                </div>
                <h3 className="text-2xl font-bold text-[#141F48] mb-4">Our Approach</h3>
                <p className="text-gray-600 leading-relaxed">
                  We believe in structured excellence, with dedicated divisions ensuring specialized expertise and focused execution, preserving our brand's core strengths.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Link to="/divisions" className="group">
              <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#141F48] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#D3BC8D] transition-colors duration-300">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141F48] mb-3">{t('divisions')}</h3>
                  <p className="text-gray-600">{t('divisionsSubtitle')}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/projects" className="group">
              <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#141F48] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#D3BC8D] transition-colors duration-300">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141F48] mb-3">{t('projects')}</h3>
                  <p className="text-gray-600">{t('projectsSubtitle')}</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/news" className="group">
              <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#141F48] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#D3BC8D] transition-colors duration-300">
                    <Newspaper className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#141F48] mb-3">{t('news')}</h3>
                  <p className="text-gray-600">{t('newsSubtitle')}</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#D3BC8D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#141F48] mb-6">
            {t('contactTitle')}
          </h2>
          <p className="text-lg text-[#141F48]/80 mb-8">
            {t('contactSubtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#141F48] text-white hover:bg-[#0f1a3d] font-semibold">
              {t('contact')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;